import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarCheck2, ChevronLeft, ChevronRight, Clock3, Footprints, IndianRupee, MapPinned } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { Skeleton } from "../../components/ui/skeleton";
import { api, errMsg, fmtDay, istDateISO } from "../../lib/api";
import { MyLeaveRequests } from "../../components/attendance/LeaveRequests";
import { RulesSummaryCard } from "../../components/attendance/Rules";
import { STATUS_META, STATUS_ORDER, StatusBadge, WEEKDAYS, dayISO, daysInMonth, inr, monthLabel, n, shiftMonth, weekdayOf } from "../../components/attendance/shared";

/** Phase 168 — collector portal: today's progress towards the requirement, month calendar, summary, leave requests. */
export default function MyAttendance() {
  const { code } = useParams();
  const today = istDateISO();
  const [month, setMonth] = useState(today.slice(0, 7));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => {
    api.get(`/t/${code}/attendance/me`, { params: { month } }).then((r) => { setData(r.data); setError(null); }).catch((e) => setError(errMsg(e)));
  }, [code, month]);
  useEffect(() => { setData(null); load(); }, [load]);

  const byDay = Object.fromEntries((data?.records || []).map((r) => [r.day, r]));
  const prog = data?.today_progress;
  const first = weekdayOf(dayISO(month, 1));
  const cells = [...Array(first).fill(null), ...Array.from({ length: daysInMonth(month) }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  const sel = selected ? byDay[selected] : null;
  const s = data?.summary;

  return (
    <div className="space-y-4" data-testid="my-attendance-page">
      <div>
        <h1 className="text-lg font-semibold font-heading">My attendance</h1>
        <p className="text-sm text-muted-foreground">Present when you complete {data?.rules?.visit_threshold ?? "…"} visits <b>or</b> collect {data?.rules ? inr(data.rules.collection_threshold) : "…"} in a day. Sign in by {data?.rules?.effective_cutoff_time || "…"} IST.</p>
      </div>

      {error && <Card className="border-destructive/40"><CardContent className="p-3 text-sm text-destructive flex items-center justify-between"><span>{error}</span><Button size="sm" variant="outline" onClick={load} data-testid="my-attendance-retry">Retry</Button></CardContent></Card>}

      {/* today */}
      <Card className="shadow-sm" data-testid="today-progress-card">
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium flex items-center gap-2"><CalendarCheck2 className="h-4 w-4 text-primary" aria-hidden="true" /> Today · {fmtDay(today)}</p>
            {!data ? <Skeleton className="h-5 w-20" /> : prog?.status ? <StatusBadge status={prog.status} testId="today-status" /> : <Badge variant="outline" className="text-[11px]">No record</Badge>}
          </div>
          {!data ? <Skeleton className="h-16 w-full" /> : prog?.status && prog.day_kind === "working" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Footprints className="h-3 w-3" /> Visits</p>
                  <p className="text-base font-semibold tabular-nums" data-testid="today-visits">{n(prog.visits)} <span className="text-xs font-normal text-muted-foreground">/ {n(prog.visit_target)}</span></p>
                  <Progress value={prog.visits_pct || 0} className="h-2 mt-1" aria-label="Visits progress" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><IndianRupee className="h-3 w-3" /> Collection</p>
                  <p className="text-base font-semibold tabular-nums" data-testid="today-collection">{inr(prog.collection)} <span className="text-xs font-normal text-muted-foreground">/ {inr(prog.collection_target)}</span></p>
                  <Progress value={prog.collection_pct || 0} className="h-2 mt-1" aria-label="Collection progress" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Clock3 className="h-3 w-3" /> Signed in {prog.login_time_ist ? <b className="text-foreground tabular-nums">{prog.login_time_ist}</b> : <b className="text-foreground">not yet</b>}{prog.late && <Badge variant="outline" className="ml-1 text-[10px] border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))]" data-testid="today-late">Late{prog.late_ordinal ? ` #${prog.late_ordinal} of ${prog.late_limit}` : ""}</Badge>}</span>
                {prog.field_duty && <span className="inline-flex items-center gap-1 text-[hsl(var(--info))]"><MapPinned className="h-3 w-3" /> Field duty today</span>}
                <span data-testid="today-reason">{prog.status_reason}</span>
              </div>
              {data.late_count_month != null && <p className="text-[11px] text-muted-foreground">Late sign-ins this month: <b className="text-foreground" data-testid="late-count-month">{data.late_count_month}</b> of {data.rules.late_login_limit} tolerated{data.late_count_month > data.rules.late_login_limit ? " — further late sign-ins count as Half Day" : ""}.</p>}
            </>
          ) : <p className="text-sm text-muted-foreground" data-testid="today-off">{prog?.status_reason || "No attendance is expected today."}</p>}
        </CardContent>
      </Card>

      {/* calendar */}
      <Card className="shadow-sm">
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => { setSelected(null); setMonth((m) => shiftMonth(m, -1)); }} aria-label="Previous month" data-testid="my-attendance-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <h2 className="text-sm font-semibold font-heading" data-testid="my-attendance-month">{monthLabel(month)}</h2>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => { setSelected(null); setMonth((m) => shiftMonth(m, 1)); }} disabled={month >= today.slice(0, 7) && shiftMonth(month, 1) > shiftMonth(today.slice(0, 7), 1)} aria-label="Next month" data-testid="my-attendance-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          {!data ? <Skeleton className="h-56 w-full" /> : (
            <>
              <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-medium text-muted-foreground">{WEEKDAYS.map((w) => <div key={w}>{w}</div>)}</div>
              <div className="grid grid-cols-7 gap-1" data-testid="my-attendance-calendar">
                {cells.map((d, i) => {
                  if (!d) return <div key={`e${i}`} />;
                  const iso = dayISO(month, d);
                  const r = byDay[iso];
                  const m = r ? STATUS_META[r.status] : null;
                  const isFuture = iso > today;
                  return (
                    <button key={iso} type="button" onClick={() => setSelected(iso === selected ? null : iso)} data-testid={`my-day-${iso}`} data-status={r?.status || ""}
                      className={`aspect-square rounded-md border text-xs flex flex-col items-center justify-center gap-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected === iso ? "ring-2 ring-primary" : ""} ${iso === today ? "border-primary" : "border-border"} ${isFuture && !r ? "text-muted-foreground/60" : "hover:bg-muted/50"}`}
                      aria-label={`${fmtDay(iso)}${r ? `: ${m.label}` : ""}`}>
                      <span className="tabular-nums font-medium">{d}</span>
                      {m && <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground pt-1">
                {STATUS_ORDER.map((k) => <span key={k} className="inline-flex items-center gap-1"><span className={`h-1.5 w-1.5 rounded-full ${STATUS_META[k].dot}`} /> {STATUS_META[k].label}</span>)}
              </div>
              {sel && (
                <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1" data-testid="my-day-detail">
                  <div className="flex items-center justify-between gap-2"><span className="font-medium">{fmtDay(sel.day)}</span><StatusBadge status={sel.status} testId="my-day-detail-status" /></div>
                  {!sel.future && sel.day_kind === "working" && <p className="text-xs text-muted-foreground tabular-nums">{n(sel.visits)} visits · {inr(sel.collection)} · signed in {sel.login_time_ist || "—"}{sel.late ? ` (late${sel.late_ordinal ? ` #${sel.late_ordinal}` : ""})` : ""}</p>}
                  <p className="text-xs" data-testid="my-day-detail-reason">{sel.status_reason}</p>
                  {sel.override && <p className="text-[11px] text-amber-800">Changed by {sel.override.admin_name}: {sel.override.reason}</p>}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* month summary */}
      {s && (
        <div className="grid grid-cols-3 gap-2" data-testid="my-attendance-summary">
          {[["Present", s.present_like, "present"], ["Half day", s.counts.half_day, "half-day"], ["Absent", s.counts.absent, "absent"], ["Leave", s.counts.leave, "leave"], ["Visits", n(s.visits), "visits"], ["Collected", inr(s.collection), "collection"]].map(([l, v, k]) => (
            <Card key={k} className="shadow-sm"><CardContent className="p-2.5"><p className="text-[11px] text-muted-foreground">{l}</p><p className="text-base font-semibold tabular-nums" data-testid={`my-summary-${k}`}>{v}</p></CardContent></Card>
          ))}
        </div>
      )}

      <MyLeaveRequests code={code} items={data?.leave_requests} onChanged={load} compact />
      {data?.rules && <RulesSummaryCard rules={data.rules} compact testId="my-attendance-rules" />}
    </div>
  );
}
